// AttachmentPicker.jsx — 工单附件的挑选控件（第五十六批，从 SubmitTicketPage 抽出）。
//
// 为什么要抽：全站唯一的 <input type="file"> 原本长在提交表单里，工单建好之后就再没有
// 补传入口——而后端 pending / info_requested 两态都允许上传。美术因此撤回 AST-000008、
// 重开 AST-000009 补 7 个文件（34 MiB），烧掉一个工单号、断掉请补线索。控件抽出来之后，
// 提交面与「我的提交」详情面共用同一个，界面不再比接口窄。
//
// 三件事是这个控件的存在理由，删任何一件都会退回事故现场：
//   1) 图片给缩略图。手机相册的 IMG_4021.jpg 之间只看文件名分不出来，选错了也不知道。
//      objectURL 必须在项目移除/组件卸载时 revoke —— 一次挑十张视频不释放就是几百 MB
//      挂在页面上直到刷新。
//   2) 每一项自己带状态。上传失败的留在列表里、标红、给出后端原话，能单独重试或删掉。
//   3) 尺寸写在名字旁边。这是"我挑对了吗"唯一可核对的线索。
//
// 第五十八批 C1 追加的第四件事：**挑选时就把不合格的文件拦下来**，并且当场说清是
// 哪一个、多大、上限多少。此前只有数量是前置校验，类型与容量要等后端 400——AST-000011
// 因此丢了一张 26 MiB 的实拍照，正文那张"按附件顺序排列"的 10 行表从第 3 行起全部错位。
// 被拒的文件**留在列表里**（标红 + 原因 + 可移除），绝不静默丢弃；同时给出上传序号，
// 让人能把屏幕上的一行和正文清单里的一行对上。
//
// 没有图标库（本仓库不依赖 @mui/icons-material），角标与删除键一律用单色字形。
// 第五十九批 E1 追加的第五件事：**每个文件一个说明框**，提交后由页面按上传成功的附件
// 生成一张机器可读的对照清单（attachments.mjs 的 noteRows/renderNotesBlock）。
// 需求方原话：「在页面端增加为每个附件添加备注的 UI，在点提交按钮时再生成跟附件对应的
// json 或 md 表格数据附在正文末尾」——目的是躲开手打表格的繁琐，也避免 AST-000011 那种
// 错位。说明框长在**这一行**上，与文件同生共死：文件被移除，它的说明跟着消失；文件没
// 传上去，它的说明就不会进清单（而是被明说出来），绝不会顶替下一个文件的位置。
import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import {
  ATTACH_ACCEPT,
  MAX_ATTACHMENTS,
  NOTE_MAX,
  addFiles,
  attachHelperText,
  attachIndexes,
  attachLabelText,
  badgeText,
  canPreview,
  failedItems,
  fmtSize,
  rejectText,
  rejectedItems,
  removeAt,
  setItemNote,
} from '../pages/attachments.mjs';
import { useT } from '../i18n/index.mjs';

// 缩略图边长。够看清是哪张照片，又不至于把表格里的详情面撑开。
const THUMB = 44;

export default function AttachmentPicker({
  items = [],
  onChange,
  disabled = false,
  max = MAX_ATTACHMENTS,
  accept = ATTACH_ACCEPT,
  label,                 // 省略则用（由允许名单表生成的）默认标签；传 null 则整块不渲染
  helper,                // 省略则用（由允许名单表生成的）默认说明；传 null 则整块不渲染
  addLabel,              // 省略则用 submit.attach.add
  onRetry = null,        // 传了才显示"重试失败项"；由调用侧决定重传到哪张工单
  // 每单总额度。补传面传进来之后，max 表示"这张工单还能再传几个"（剩余额度），
  // 界面就得按剩余额度说话——否则"最多 3 个附件"读起来像规则变了（第五十七批 D4）。
  quotaTotal = null,
  // 序号的起点：这张工单**已有**几个附件。补传面从第 (base+1) 个开始数，提交面为 0。
  indexBase = 0,
  // 逐附件说明（E1）。传 false 则整列不渲染——只在"说明真的送得出去"的界面上开，
  // 收了却送不出去比不收更坏（用户以为写过了）。
  notes = true,
}) {
  const t = useT();
  // 超额被丢弃的个数：静默截断等于又丢一次文件，所以要留在界面上说清楚。
  const [overflow, setOverflow] = useState(0);

  // objectURL 按项 key 缓存在 ref 里：放 state 会让"创建 URL"进入渲染路径，
  // 而 revoke 的时机必须由**项目消失**驱动，不是由渲染驱动。
  const urlsRef = useRef(new Map());
  const [, bump] = useState(0);

  useEffect(() => {
    const m = urlsRef.current;
    const live = new Set();
    let changed = false;
    for (const it of items) {
      if (!canPreview(it.name)) continue;
      live.add(it.key);
      if (!m.has(it.key)) {
        try {
          m.set(it.key, URL.createObjectURL(it.file));
          changed = true;
        } catch {
          /* 非浏览器环境或文件已失效：退回角标，不影响上传 */
        }
      }
    }
    for (const [k, url] of [...m.entries()]) {
      if (live.has(k)) continue;
      URL.revokeObjectURL(url);         // 移除一项就立刻还回去，别攒到卸载
      m.delete(k);
      changed = true;
    }
    if (changed) bump((n) => n + 1);
  }, [items]);

  // 卸载兜底：详情面是折叠即卸载的，靠上面那轮 diff 收不干净。
  useEffect(() => {
    const m = urlsRef.current;
    return () => {
      for (const url of m.values()) URL.revokeObjectURL(url);
      m.clear();
    };
  }, []);

  const failed = failedItems(items);
  const rejected = rejectedItems(items);
  // 「它会成为这张工单的第几个附件」。被拒的项不占序号——占了的话屏幕上的编号就和
  // 工单上的实际顺序错开一位，正是 AST-000011 那种错位。
  const indexes = attachIndexes(items, indexBase);
  const quotaKnown = quotaTotal != null;
  const full = max <= 0;                 // 额度用完：挑了也只会被 addFiles 全部丢掉

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    const r = addFiles(items, picked, max);
    setOverflow(r.overflow);
    onChange(r.items);
    e.target.value = '';               // 同一个文件再挑一次也要触发 change
  };

  return (
    <Box>
      {label === undefined || label ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
          {label === undefined ? attachLabelText(t) : label}
        </Typography>
      ) : null}

      <Stack sx={{ alignItems: 'center', flexWrap: 'wrap' }} direction="row" spacing={1} useFlexGap>
        <Button component="label" variant="outlined" size="small" disabled={disabled || full}>
          {addLabel === undefined ? t('submit.attach.add') : addLabel}
          {/* BUG-000001（第七十五批）：`hidden` 属性即 display:none——部分浏览器
              （Brave 实测、Safari 家族有同类报告）不把 label 点击转发给 display:none
              的文件输入，点"选择文件"毫无反应。换 MUI 官方"视觉隐藏"姿势：输入框
              仍在布局树里（可聚焦、可被 label 转发），只是视觉上 1px 裁切不可见。 */}
          <input
            type="file"
            multiple
            accept={accept}
            onChange={onPick}
            style={{
              clip: 'rect(0 0 0 0)',
              clipPath: 'inset(50%)',
              height: 1,
              width: 1,
              overflow: 'hidden',
              position: 'absolute',
              bottom: 0,
              left: 0,
              whiteSpace: 'nowrap',
            }}
          />
        </Button>
        {onRetry && failed.length > 0 ? (
          <Button variant="contained" color="warning" size="small" disabled={disabled} onClick={onRetry}>
            {t('submit.attach.retry', { n: String(failed.length) })}
          </Button>
        ) : null}
      </Stack>

      {items.length > 0 ? (
        // flexWrap + minWidth:0 —— 详情面嵌在表格单元格里，长文件名不得把页面撑出横向滚动。
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {items.map((it, i) => {
            const url = urlsRef.current.get(it.key);
            const bad = it.status === 'failed' || it.status === 'rejected';
            const idx = indexes[i];
            // 长文本单独一行、可换行：被拒原因里有文件名 + 实际大小 + 上限，用省略号
            // 截掉的话正好把"为什么"截没了，390px 上尤其明显（DP-11）。
            const detail = it.status === 'rejected' ? rejectText(t, it.reject)
              : it.status === 'failed' ? it.error : '';
            return (
              <Paper
                key={it.key}
                variant="outlined"
                sx={{
                  display: 'flex',
                  // 说明框要独占一行（flexBasis:'100%'），所以这一层必须允许换行。
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 1,
                  p: 0.75,
                  // 有说明框时卡片放宽到 320：260 里塞一个输入框只剩 ~170px，
                  // 一句「来源；类别；拍的是什么」要滚三屏才看得完。
                  width: { xs: '100%', sm: notes ? 320 : 260 },
                  maxWidth: '100%',
                  borderColor: bad ? 'error.main' : undefined,
                }}
              >
                <Box
                  sx={{
                    width: THUMB,
                    height: THUMB,
                    flex: '0 0 auto',
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {url ? (
                    <Box
                      component="img"
                      src={url}
                      alt={it.name}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      {badgeText(it.name)}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    title={it.name}
                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {it.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={bad ? 'error.main' : 'text.secondary'}
                    sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {/* 序号 + 尺寸：相册里的 IMG_4021.jpg 之间只靠文件名分不出来，而
                        "第几个附件"是把这一行和正文清单里的一行对上的唯一线索。 */}
                    {idx != null ? (
                      <Box
                        component="span"
                        sx={{ fontFamily: 'ui-monospace, monospace' }}
                        title={t('submit.attach.order', { n: String(idx) })}
                      >
                        {`#${idx} · `}
                      </Box>
                    ) : null}
                    {fmtSize(it.size)}
                    {it.status === 'failed' ? ` · ${t('submit.attach.state.failed')}` : ''}
                    {it.status === 'done' ? ` · ${t('submit.attach.state.done')}` : ''}
                    {it.status === 'rejected' ? ` · ${t('submit.attach.state.rejected')}` : ''}
                  </Typography>
                  {detail ? (
                    <Typography
                      variant="caption"
                      color="error.main"
                      sx={{ display: 'block', whiteSpace: 'normal', wordBreak: 'break-word' }}
                    >
                      {detail}
                    </Typography>
                  ) : null}
                </Box>

                <Button
                  size="small"
                  color="inherit"
                  disabled={disabled}
                  onClick={() => onChange(removeAt(items, it.key))}
                  aria-label={t('submit.attach.remove')}
                  title={t('submit.attach.remove')}
                  sx={{ minWidth: 32, px: 0.5, flex: '0 0 auto', fontSize: 16, lineHeight: 1 }}
                >
                  ×
                </Button>

                {/* 说明框独占一行（flexBasis 100% + minWidth 0）。
                    DP-11 最窄形态的实算（390px 视口，提交页这条链最深）：
                      390 − AppShell Container px:xs=2 (16×2) = 358
                      − Card 边框 1×2 − CardContent p:xs=2.5 (20×2) = 316  ← Paper 外宽
                      − Paper 边框 1×2 − Paper p:0.75 (6×2) = 302          ← 行内容宽
                    说明框拿满这 302px；它不参与那一行的 44+8+…+32 分配，所以窄屏下
                    缩略图/文件名/删除键的既有算式一格不动（名字列仍是 302−44−8−8−32=210）。
                    输入框本身 fullWidth + minWidth:0，没有内在最小宽度，不会把
                    正文顶出横滚（DP-11 铁律）。
                    被拒/失败的项也照给说明框：文件还留在列表里等重挑或重试，此刻抹掉
                    人刚写的那句话，等于逼他再写一遍——而"再写一遍"正是这次要省掉的事。 */}
                {notes && it.status !== 'done' ? (
                  <Box sx={{ flexBasis: '100%', minWidth: 0 }}>
                    <TextField
                      value={it.note || ''}
                      onChange={(e) => onChange(setItemNote(items, it.key, e.target.value))}
                      disabled={disabled}
                      label={t('submit.attach.note.label')}
                      placeholder={t('submit.attach.note.placeholder')}
                      size="small"
                      fullWidth
                      multiline
                      maxRows={4}
                      slotProps={{ htmlInput: { maxLength: NOTE_MAX } }}
                      sx={{ minWidth: 0 }}
                    />
                  </Box>
                ) : null}
                {/* 已上传的项只回显说明（只读）：它已经进了清单，此刻改这里也送不出去——
                    给一个改不动的输入框，是在允诺一件做不到的事。 */}
                {notes && it.status === 'done' && it.note ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ flexBasis: '100%', minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}
                  >
                    {it.note}
                  </Typography>
                ) : null}
              </Paper>
            );
          })}
        </Box>
      ) : null}

      {overflow > 0 ? (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.75 }}>
          {/* 丢掉了几个必须说出来（静默截断 = 又丢一次文件）；补传面还要说清是**这张工单**
              的剩余额度用完了，而不是"一次最多挑 n 个"。 */}
          {quotaKnown
            ? t('submit.attach.maxRemaining', {
                n: String(max),
                total: String(quotaTotal),
                dropped: String(overflow),
              })
            : t('submit.attach.max', { n: String(max), dropped: String(overflow) })}
        </Typography>
      ) : null}

      {rejected.length > 0 ? (
        // 每一项自己已经写了原因，这一条负责说清**后果**：它们不会跟着这次提交上去。
        // AST-000011 的正文按附件顺序列了 10 行清单，实际只落地 9 个——错位就是这么来的。
        <Alert severity="warning" sx={{ mt: 1 }}>
          {t('submit.attach.rejectedNotice', { n: String(rejected.length) })}
        </Alert>
      ) : null}

      {quotaKnown ? (
        <Typography
          variant="caption"
          color={full ? 'warning.main' : 'text.secondary'}
          sx={{ display: 'block', mt: 0.75 }}
        >
          {full
            ? t('submit.attach.quotaFull', { total: String(quotaTotal) })
            : t('submit.attach.quotaLeft', { n: String(max), total: String(quotaTotal) })}
        </Typography>
      ) : null}

      {/* 说明框在做什么，只在**有文件可写说明**时才讲——空列表上先讲一遍规则，
          读的时候还没有对应物，等真挑了文件又不会再讲一次。 */}
      {notes && items.length > 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {t('submit.attach.note.hint')}
        </Typography>
      ) : null}

      {helper === undefined ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {attachHelperText(t)}
        </Typography>
      ) : helper ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {helper}
        </Typography>
      ) : null}
    </Box>
  );
}

// ── 生成好的说明清单块 ────────────────────────────────────────────────────────
// 提交面与补传面共用一份（DP-11：同一件事写第二遍，两份就会各自漂移）。
// 为什么需要"复制"这条路：后端 me 家族的写入口只有建单 / 传附件 / 补充回复 / 撤回
// 四个（routes_me.cpp），**没有改正文**。而清单只有等附件真的传上去才算得出来，
// 那时正文已经落库——这两件事凑在一起，就决定了清单在提交这一刻进不了正文。
// 我们不假装它进去了：把它原样摆出来、可复制，并把原因讲清楚。
export function AttachmentNotesBlock({ block, rows = 0, orphans = 0, note = '', onNotify = null }) {
  const t = useT();
  if (!block && orphans <= 0) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(block);
      onNotify?.(t('submit.attach.notes.copied'), 'success');
    } catch {
      // 非安全上下文 / 用户拒绝授权：不静默失败，告诉他手动选中复制。
      onNotify?.(t('submit.attach.notes.copyFailed'), 'warning');
    }
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      {orphans > 0 ? (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {t('submit.attach.notes.orphan', { n: String(orphans) })}
        </Alert>
      ) : null}
      {block ? (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('submit.attach.notes.title', { n: String(rows) })}
          </Typography>
          {note ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {note}
            </Typography>
          ) : null}
          {/* 只读多行框而不是 <pre>：<pre> 不换行会把正文顶出横滚条（DP-11 铁律），
              而且在手机上无法方便地全选。TextField 自己带滚动与选择。 */}
          <TextField
            value={block}
            InputProps={{ readOnly: true }}
            multiline
            maxRows={12}
            fullWidth
            size="small"
            sx={{
              minWidth: 0,
              '& textarea': { fontFamily: 'ui-monospace, monospace', fontSize: 12, lineHeight: 1.5 },
            }}
          />
          <Box sx={{ mt: 1 }}>
            <Button size="small" variant="outlined" onClick={copy}>
              {t('submit.attach.notes.copy')}
            </Button>
          </Box>
        </Paper>
      ) : null}
    </Box>
  );
}
